import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  getTasks,
  createTask,
  updateTask,
  getUsers,
  getHouseholds,
  getBarangays,
} from '../../lib/api';
import {
  ClipboardList,
  Plus,
  Calendar as CalendarIcon,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
} from 'lucide-react';

const TASK_TYPES = [
  { value: 'house_visit', label: 'House Visit' },
  { value: 'vaccination_reminder', label: 'Vaccination Reminder' },
  { value: 'medicine_delivery', label: 'Medicine Delivery' },
  { value: 'maternal_care', label: 'Maternal Care' },
];

export default function TaskManagement() {
  const { user, isMunicipalAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [bhwUsers, setBhwUsers] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBHW, setFilterBHW] = useState('all');

  const [formData, setFormData] = useState({
    barangay_id: '',
    task_type: '',
    household_id: '',
    assigned_bhw: '',
    due_date: new Date(),
    notes: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksRes, usersRes, householdsRes, barangaysRes] = await Promise.all([
        getTasks(),
        getUsers({ role: 'bhw' }).catch(() => ({ data: [] })),
        getHouseholds(),
        getBarangays(),
      ]);
      setTasks(tasksRes.data);
      setBhwUsers(usersRes.data);
      setHouseholds(householdsRes.data);
      setBarangays(barangaysRes.data);

      if (!isMunicipalAdmin() && user?.barangay_id) {
        setFormData(prev => ({ ...prev, barangay_id: user.barangay_id }));
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await createTask({
        ...formData,
        due_date: formData.due_date.toISOString(),
        household_id: formData.household_id || null,
      });
      toast.success('Task created successfully');
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'completed') {
        updateData.completion_timestamp = new Date().toISOString();
      }
      await updateTask(taskId, updateData);
      toast.success('Task updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update task');
    }
  };

  const resetForm = () => {
    setFormData({
      barangay_id: !isMunicipalAdmin() ? user?.barangay_id || '' : '',
      task_type: '',
      household_id: '',
      assigned_bhw: '',
      due_date: new Date(),
      notes: '',
    });
  };

  const filteredTasks = tasks.filter(task => {
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesBHW = filterBHW === 'all' || task.assigned_bhw === filterBHW;
    return matchesStatus && matchesBHW;
  });

  const getBHWName = (id) => bhwUsers.find(u => u.id === id)?.full_name || 'Unknown';
  const getHouseholdAddress = (id) => households.find(h => h.id === id)?.address_label || '';
  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || 'Unknown';

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => t.status !== 'completed' && new Date(t.due_date) < new Date()).length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="task-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              BHW Task Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Assign and track barangay health worker tasks
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Task
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Task</DialogTitle>
                <DialogDescription>
                  Assign a task to a barangay health worker
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                {isMunicipalAdmin() && (
                  <div className="space-y-2">
                    <Label>Barangay *</Label>
                    <Select
                      value={formData.barangay_id}
                      onValueChange={(v) => setFormData({ ...formData, barangay_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select barangay" />
                      </SelectTrigger>
                      <SelectContent>
                        {barangays.map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Task Type *</Label>
                  <Select
                    value={formData.task_type}
                    onValueChange={(v) => setFormData({ ...formData, task_type: v })}
                  >
                    <SelectTrigger data-testid="task-type-select">
                      <SelectValue placeholder="Select task type" />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assign to BHW *</Label>
                  <Select
                    value={formData.assigned_bhw}
                    onValueChange={(v) => setFormData({ ...formData, assigned_bhw: v })}
                  >
                    <SelectTrigger data-testid="bhw-select">
                      <SelectValue placeholder="Select BHW" />
                    </SelectTrigger>
                    <SelectContent>
                      {bhwUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Household (Optional)</Label>
                  <Select
                    value={formData.household_id || '__none__'}
                    onValueChange={(v) => setFormData({ ...formData, household_id: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select household" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {households.filter(h => h.qr_status === 'ACTIVE').map((h) => (
                        <SelectItem key={h.id} value={h.id}>{h.address_label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.due_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.due_date}
                        onSelect={(date) => setFormData({ ...formData, due_date: date || new Date() })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional instructions..."
                    data-testid="task-notes-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading} data-testid="submit-task-btn">
                    {formLoading ? 'Creating...' : 'Create Task'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.pending > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className={`h-6 w-6 ${stats.pending > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.overdue > 0 ? 'border-red-300 bg-red-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-6 w-6 ${stats.overdue > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.overdue}</p>
                  <p className="text-xs text-muted-foreground">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterBHW} onValueChange={setFilterBHW}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-bhw">
                  <SelectValue placeholder="Filter by BHW" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All BHWs</SelectItem>
                  {bhwUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              Tasks
            </CardTitle>
            <CardDescription>
              {filteredTasks.length} tasks found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Household</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task) => {
                    const isOverdue = task.status !== 'completed' && new Date(task.due_date) < new Date();
                    
                    return (
                      <TableRow 
                        key={task.id} 
                        data-testid={`task-row-${task.id}`}
                        className={isOverdue ? 'bg-red-50/50' : ''}
                      >
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {task.task_type.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {getBHWName(task.assigned_bhw)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {getHouseholdAddress(task.household_id) || '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                          {new Date(task.due_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {task.status !== 'completed' && (
                            <div className="flex gap-1">
                              {task.status === 'pending' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateStatus(task.id, 'in_progress')}
                                  data-testid={`start-task-${task.id}`}
                                >
                                  Start
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleUpdateStatus(task.id, 'completed')}
                                data-testid={`complete-task-${task.id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Complete
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No tasks found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
